// AICA API Utilities
// Error handling, retry logic, and typed fetch wrappers
// Priority 1.4.3: API Error Handling Pattern
//
// H7 FIX: All fetch methods now include X-Selected-Org-ID header
// for multi-tenant security. The org ID is retrieved from sessionStorage.

import {
  layerHeaders,
  type AuthenticatedFetchOptions,
  type ExpectedRefusal,
} from './request-headers';
import { getCSRFToken, refreshCSRFToken, isCSRFTokenExpired } from './csrf';
import { isOrphanedSessionBody, notifyOrphanedSession } from './orphaned-session';
import { notifyEmbeddedSessionExpired } from './embedded-session-recovery';
import {
  StaleAccessRevisionError,
  accessRevisionRequestHeaders,
  beginAccessFence,
  judgeAccessResponse,
  type AccessFence,
} from './access-revision';
import { logError, logWarning } from '@/lib/logger';
import { API_BASE_URL } from '@/lib/api/config';

/**
 * Resolve an API path against the configured edge-api base URL.
 *
 * Relative `/api/*` paths must be prefixed because the UI worker's
 * domain proxies /api/* to edge-api (same-origin, no preflights); an
 * explicit VITE_API_URL targets an API origin directly. Absolute URLs
 * pass through.
 */
function resolveApiUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/api/')) return `${API_BASE_URL}${url}`;
  return url;
}

// ============================================================================
// Organization ID Helper (H7 Fix)
// ============================================================================

const SESSION_STORAGE_ORG_KEY = 'aica_selected_org_id';

/**
 * Get the selected organization ID from sessionStorage
 *
 * This allows fetch helpers to include X-Selected-Org-ID header
 * even when called outside React context.
 *
 * @returns The selected org ID or null if not set
 */
export function getSelectedOrgIdFromStorage(): string | null {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(SESSION_STORAGE_ORG_KEY);
}

const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

function normalizeMethod(method?: string): string {
  return (method ?? 'GET').toUpperCase();
}

function isIdempotentMethod(method?: string): boolean {
  return IDEMPOTENT_METHODS.has(normalizeMethod(method));
}

function hasIdempotencyKey(headers?: HeadersInit): boolean {
  if (!headers) return false;
  return new Headers(headers).has('Idempotency-Key');
}

/**
 * Extract a human-readable message from a parsed API error body.
 *
 * Handles every envelope the backend emits:
 *  - `{ message: '...' }`           (simple)
 *  - `{ error: '...' }`             (simple string)
 *  - `{ error: { message: '...' } }` (standard createErrorResponse envelope)
 *
 * Returns '' when no usable message is present, so callers can fall back to an
 * HTTP-status string. Without the nested case, every 4xx from the standard
 * envelope surfaced as a bare "HTTP 403:" with an empty statusText (AU-24).
 */
export function extractApiErrorMessage(errorData: unknown): string {
  if (!errorData || typeof errorData !== 'object') return '';
  const data = errorData as Record<string, unknown>;
  if (typeof data.message === 'string' && data.message) return data.message;
  if (typeof data.error === 'string' && data.error) return data.error;
  if (data.error && typeof data.error === 'object') {
    const nested = data.error as Record<string, unknown>;
    if (typeof nested.message === 'string' && nested.message) return nested.message;
  }
  return '';
}

function canRetryRequest(method?: string, headers?: HeadersInit): boolean {
  return isIdempotentMethod(method) || hasIdempotencyKey(headers);
}

/**
 * The caller's own signal ended the request: a plain abort, or the
 * deadline `AbortSignal.timeout()` / `controller.abort(new DOMException(…,
 * 'TimeoutError'))` raises. Neither is a failure of the request, so neither
 * is repeated (owner's review, 17.09 — `TimeoutError` was not recognised
 * and would have been retried by any caller that allows retries).
 */
const CALLER_ENDED = new Set(['AbortError', 'TimeoutError']);

function isAbortError(error: unknown): boolean {
  if (!error) return false;
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return CALLER_ENDED.has(error.name);
  }
  return error instanceof Error && CALLER_ENDED.has(error.name);
}

/**
 * A caller's `RequestInit.headers`, whatever its shape, as the record
 * `layerHeaders` layers — one merge rule for the helpers below and the
 * authenticated wrappers (owner's review, 17.09). A record keeps its key
 * casing; `Headers` and tuples come back lower-cased, which is how the
 * wire sees them anyway.
 */
function toHeaderRecord(init?: HeadersInit): Record<string, string> {
  if (!init) return {};
  if (!(init instanceof Headers) && !Array.isArray(init)) return { ...init };
  const record: Record<string, string> = {};
  new Headers(init).forEach((value, key) => {
    record[key] = value;
  });
  return record;
}

/**
 * Get default headers for API requests
 *
 * H7 FIX: Always includes X-Selected-Org-ID if available.
 * This ensures multi-tenant security across all API calls.
 */
function getDefaultHeaders(method?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  const normalizedMethod = normalizeMethod(method);
  if (['POST', 'PUT', 'PATCH'].includes(normalizedMethod)) {
    headers['Content-Type'] = 'application/json';
  }

  // H7 FIX: Always add org header if available
  const orgId = getSelectedOrgIdFromStorage();
  if (orgId) {
    headers['X-Selected-Org-ID'] = orgId;
  }

  return headers;
}

// ============================================================================
// Retry Configuration Constants
// ============================================================================

/** Base delay for exponential backoff (milliseconds) */
const RETRY_BASE_DELAY_MS = 1000;
/** Maximum delay between retries (30 seconds) */
const RETRY_MAX_DELAY_MS = 30000;
/** Delay in test environment (fast tests) */
const RETRY_TEST_DELAY_MS = 10;
/**
 * Longest `Retry-After` this client will actually sleep through (60 seconds).
 *
 * The value is not a matter of taste. The only refusal a user can plausibly
 * wait out in an open tab is the per-minute limiter, whose window is exactly
 * 60s. Everything else we measured is far longer — the re-analysis cooldown is
 * 300s, the hourly limiter 3600s, the daily one 86400s — and sleeping on those
 * turns a click into a tab that sits silent for minutes, which reads as a hang
 * and gets clicked again. Past the cap the right answer is not a longer sleep
 * but a fast refusal that says how long to wait.
 *
 * It also contains a defect we have measured rather than imagined: a server
 * that sends `Retry-After` in the wrong unit yields a delay of ~1.78e15 ms,
 * which does not fit a 32-bit signed integer, so `setTimeout` silently clamps
 * it to 1ms and the "wait" becomes an immediate retry storm against a limiter
 * that is already refusing. Any such value is far past this cap and now fails
 * fast instead.
 */
export const RETRY_AFTER_MAX_DELAY_MS = 60_000;

/**
 * `Retry-After` as milliseconds until the caller may repeat, or undefined.
 *
 * Either delta-seconds or an HTTP date (RFC 9110 §10.2.3); both forms occur in
 * practice, so both are read. Anything else — absent, unparseable, zero, or in
 * the past — yields undefined, and the caller falls back to its own delay
 * rather than to zero, which would repeat the request instantly into the same
 * closed window.
 *
 * The one home for this parse. Both retry transports read `Retry-After` — this
 * one on the fetch path (`fetchWithRetry` below) and the XHR upload path
 * (`useXHRWithCSRF`) — and a second copy drifts silently on exactly the rare
 * branch it exists for: an earlier inline copy in the 429 branch used
 * `Number.parseFloat`, which turns `"30x"` into 30 where this returns
 * undefined. Same header, two different waits. One function keeps the two
 * transports honest — the same reasoning as `isCSRFTokenExpired`.
 */
export function parseRetryAfter(headerValue: string | null): number | undefined {
  if (!headerValue) return undefined;

  const seconds = Number(headerValue.trim());
  if (Number.isFinite(seconds)) {
    return seconds > 0 ? seconds * 1000 : undefined;
  }

  const date = Date.parse(headerValue);
  if (Number.isNaN(date)) return undefined;
  const delta = date - Date.now();
  return delta > 0 ? delta : undefined;
}

/**
 * Custom API Error class with additional context
 */
/**
 * The server answered, but not with the shape the client's schema promises
 * (Zod = SSOT): a deterministic failure — asking again returns the same
 * body — unlike a network failure, which also carries no status.
 * `isTransientFailure` tells the two apart by this class (owner's review,
 * 17.09).
 */
export class ApiContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiContractError';
  }
}

export class ApiError extends Error {
  public data?: unknown;
  /**
   * How long the server asked the caller to wait, in milliseconds, when it
   * refused with 429 and named a delay. Present only when the delay exceeded
   * `RETRY_AFTER_MAX_DELAY_MS` and the request was therefore refused instead of
   * retried — so a caller can tell the user how long to wait rather than
   * showing a bare "too many requests".
   */
  public retryAfterMs?: number;
  /**
   * Set when an embedded (Bitrix24) 401 was claimed by session recovery, which
   * is already re-entering through the entry point — so this failure is an
   * expected, self-repairing outcome rather than something to page a human
   * about.
   *
   * It rides on the ERROR, not just on the log context, because the two doors
   * into Sentry are not equivalent: the log line reaches `captureMessage` with
   * its context in `extra`, but the same ApiError also reaches
   * `captureException` (ErrorBoundary, unhandled rejection) carrying nothing
   * but itself. Without this flag that second door has only the message text
   * to go on — which is how the filter came to match a branch production never
   * takes (see `isExpectedEmbeddedSessionExpiry`).
   */
  public handledByEmbeddedRecovery?: boolean;

  constructor(
    message: string,
    public status?: number,
    public statusText?: string,
    public response?: Response
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Exponential backoff delay calculation
 */
const isTestEnvironment =
  typeof process !== 'undefined' &&
  process.env !== undefined &&
  (process.env.VITEST === 'true' || process.env.JEST_WORKER_ID !== undefined);

function getRetryDelay(attemptIndex: number): number {
  if (isTestEnvironment) {
    return RETRY_TEST_DELAY_MS;
  }
  return Math.min(RETRY_BASE_DELAY_MS * 2 ** attemptIndex, RETRY_MAX_DELAY_MS);
}

/**
 * Check if error is retryable
 *
 * IMPORTANT: We do NOT retry 5xx errors because:
 * 1. If server is broken, retrying makes it worse (more load)
 * 2. Multiple parallel requests × retries = cascade failure
 * 3. 500 errors need investigation, not retry
 *
 * We only retry:
 * - Network errors (no status) - temporary connection issues
 * - 503 Service Unavailable - temporary overload, may recover
 *
 * A query layer may still ask a 500/502/504 ONCE for a read whose failure is
 * read as an answer, not shown as an error (`retryServerFaultOnce`,
 * lib/query-retry.ts) — never by a bare `retry: N`.
 *
 * NOTE: Retries decided HERE only happen for idempotent methods (or if
 * Idempotency-Key is present) — see `canRetryRequest`. That gate is right for
 * this function and wrong for 429, which is why 429 is handled separately and
 * deliberately ungated. The distinction is worth stating, because a reader who
 * finds an ungated retry will otherwise read it as an oversight and "fix" it:
 *
 *   The question is not which status arrived, it is **whether the server did
 *   any work before refusing**.
 *
 * - **Refused before processing** (429 from the rate-limit middleware, which
 *   returns before `next()` ever runs). Nothing was committed, so a repeat
 *   cannot duplicate anything, whatever the method — a POST that was rate
 *   limited never reached the handler. Idempotency is irrelevant here; the
 *   only requirement is that the repeat DIFFER from the request that failed,
 *   which for 429 means waiting as long as `Retry-After` says.
 * - **Outcome unknown** (network failure, 503). The request may well have been
 *   processed and only the answer lost. Repeating it can duplicate the work,
 *   so these stay behind `canRetryRequest` and need the idempotency guarantee.
 *
 * The same axis governs the XHR upload path in `useRecordsUpload`; the two
 * transports are meant to agree, and a change to one belongs in both.
 */
function isRetryableError(status?: number): boolean {
  if (!status) return true; // Network errors (no response) are retryable
  if (status === 503) return true; // Service temporarily unavailable
  return false; // All other errors (including 500, 502, 504) are NOT retried
}

/** Does the answer match one of the refusals the caller declared as expected (#1751)? */
function isExpectedRefusal(
  status: number,
  errorData: unknown,
  expected: readonly ExpectedRefusal[]
): boolean {
  if (expected.length === 0) return false;
  const { code, error } =
    typeof errorData === 'object' && errorData !== null
      ? (errorData as { code?: unknown; error?: unknown })
      : ({} as { code?: unknown; error?: unknown });
  return expected.some(
    (refusal) =>
      refusal.status === status &&
      (refusal.code === undefined || refusal.code === code) &&
      (refusal.error === undefined || refusal.error === error)
  );
}

/**
 * Fetch wrapper with automatic retry for failed requests
 */
export async function fetchWithRetry(
  url: string,
  options?: RequestInit,
  maxRetries = 3,
  fence: AccessFence | null = null,
  expectedRefusals: readonly ExpectedRefusal[] = []
): Promise<Response> {
  let lastError: Error | null = null;
  let csrfRetried = false;
  // Create a mutable copy of options to allow header updates
  let requestOptions: RequestInit = options ? { ...options } : {};
  const method = normalizeMethod(requestOptions.method);
  const allowRetry = canRetryRequest(method, requestOptions.headers);

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, requestOptions);

      // Handle undefined response (can happen in test mocks)
      if (!response) {
        throw new Error('Network error: No response from server');
      }

      // The access revision the answer carries (E1.5 PR 4b): a late GET answer
      // of an older revision is refused here, before any branch below can turn
      // it into a screen's data or an error message; a newer one is learned,
      // from writes too. Refusals (403 `capability_required`) carry it as well.
      judgeAccessResponse(fence, response.headers);

      // If response is OK, return it
      if (response.ok) {
        return response;
      }

      // Handle rate limiting (429) with respect to Retry-After header
      if (response.status === 429) {
        // Held as ApiError rather than through `lastError`, whose declared type
        // is the base Error: the rate-limit branch is the one place that has a
        // wait to attach, and narrowing it here keeps that fact in the types.
        const rateLimitError = new ApiError(
          `HTTP ${response.status}: ${response.statusText || 'Too Many Requests'}`,
          response.status,
          response.statusText,
          response
        );
        // The refusal body is a verdict the caller may act on — the assistant's
        // `quota_exceeded` names the window that tripped and when it resets
        // (§2.2) — so it rides on the error exactly as the non-retryable branch
        // attaches its `data`. A body that is not JSON is simply absent.
        try {
          const body: unknown = await response.clone().json();
          if (body !== null && typeof body === 'object') rateLimitError.data = body;
        } catch {
          // No JSON body — a bare 429 stays a bare 429.
        }
        lastError = rateLimitError;

        // Deliberately not gated on `allowRetry`: the limiter refuses before
        // the handler runs, so there is no work to duplicate. See the axis in
        // the isRetryableError docblock before adding a gate back.
        if (attempt === maxRetries) {
          break;
        }

        // The same parse the XHR upload path uses — see parseRetryAfter.
        // undefined means the server named no delay we can act on, so the
        // fallback below is our own exponential backoff.
        const retryAfterMs = parseRetryAfter(response.headers.get('Retry-After'));

        // A delay we cannot sit through is a message, not a sleep: refuse now
        // and carry the wait so the caller can say how long it is.
        if (retryAfterMs !== undefined && retryAfterMs > RETRY_AFTER_MAX_DELAY_MS) {
          rateLimitError.retryAfterMs = retryAfterMs;
          break;
        }

        const retryDelay = retryAfterMs ?? getRetryDelay(attempt);

        logError('Rate limited, retrying request', {
          module: 'API',
          function: 'fetchWithRetry',
          attempt,
          retryDelay,
        });

        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        continue;
      }

      // Handle CSRF token expiry (403) - retry once with new token
      if (response.status === 403 && !csrfRetried) {
        try {
          const clonedResponse = response.clone();
          const body = (await clonedResponse.json()) as { error?: string; code?: string };
          // Shared with the XHR upload path — see isCSRFTokenExpired for why
          // the code, and not the message text, is what decides this.
          if (isCSRFTokenExpired(response.status, body.code)) {
            logError('CSRF token expired, refreshing...', {
              module: 'API',
              function: 'fetchWithRetry',
              attempt,
            });
            csrfRetried = true;
            await refreshCSRFToken();

            // Small delay to ensure cookie is set by browser
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Update headers with new CSRF token for state-changing methods
            if (
              requestOptions.method &&
              ['POST', 'PUT', 'DELETE', 'PATCH'].includes(
                requestOptions.method.toUpperCase()
              )
            ) {
              const newCsrfToken = await getCSRFToken();

              // Create a new headers object with updated CSRF token
              if (requestOptions.headers) {
                if (requestOptions.headers instanceof Headers) {
                  // Clone Headers object and update CSRF token
                  const newHeaders = new Headers(requestOptions.headers);
                  newHeaders.set('X-CSRF-Token', newCsrfToken);
                  requestOptions.headers = newHeaders;
                } else if (Array.isArray(requestOptions.headers)) {
                  // Clone array and update CSRF token
                  const newHeaders = [...requestOptions.headers];
                  const headerIndex = newHeaders.findIndex(
                    ([key]) => key.toLowerCase() === 'x-csrf-token'
                  );
                  if (headerIndex >= 0) {
                    newHeaders[headerIndex] = ['X-CSRF-Token', newCsrfToken];
                  } else {
                    newHeaders.push(['X-CSRF-Token', newCsrfToken]);
                  }
                  requestOptions.headers = newHeaders;
                } else {
                  // Clone object and update CSRF token
                  requestOptions.headers = {
                    ...requestOptions.headers,
                    'X-CSRF-Token': newCsrfToken,
                  };
                }
              } else {
                // Create new headers object with CSRF token
                requestOptions.headers = {
                  'X-CSRF-Token': newCsrfToken,
                };
              }

              // Ensure credentials: 'include' is set for CSRF cookie to be sent
              requestOptions.credentials = 'include';
            }

            // Retry this attempt with the new token
            attempt--;
            continue;
          }
        } catch (parseError) {
          // If we can't parse the body, continue with normal error handling
          logError(
            'Failed to parse CSRF error response',
            { module: 'API', function: 'fetchWithRetry', attempt },
            parseError instanceof Error ? parseError : undefined
          );
        }
      }

      // For non-retryable errors (4xx), try to parse debug info before throwing
      if (!isRetryableError(response.status)) {
        // Try to extract debug info from response body
        let errorData: unknown = null;
        let detailedMessage = '';
        try {
          const clonedResponse = response.clone();
          errorData = await clonedResponse.json();
          detailedMessage = extractApiErrorMessage(errorData);
        } catch {
          // Failed to parse error response body - continue without debug info
        }

        // Use detailed message if available, otherwise fall back to HTTP status
        const errorMessage =
          detailedMessage || `HTTP ${response.status}: ${response.statusText}`;
        const error = new ApiError(
          errorMessage,
          response.status,
          response.statusText,
          response
        );

        // Attach parsed error data to ApiError for easier access
        if (errorData && typeof errorData === 'object' && errorData !== null) {
          (error as ApiError & { data?: unknown }).data = errorData;
        }

        // A live Clerk session whose user row was deleted server-side 401s on
        // every call — hand it to the sign-out bridge instead of letting
        // background polls storm forever. No-op in embedded mode (no handler).
        if (response.status === 401 && isOrphanedSessionBody(errorData)) {
          notifyOrphanedSession();
        }

        // An embedded (Bitrix24) 401 means the one-hour session cookie lapsed on
        // an idle tab. Recovery re-enters through the Bitrix24 entry point with a
        // freshly minted AUTH_ID; when it takes ownership the 401 is an expected,
        // already-answered outcome rather than something to page a human about.
        const embeddedRecoveryStarted =
          response.status === 401 && notifyEmbeddedSessionExpired();
        if (embeddedRecoveryStarted) {
          error.handledByEmbeddedRecovery = true;
        }

        // Single consolidated log for API errors. 403/404 are expected
        // outcomes of role-gated UI and stale deep links (RBAC denial, record
        // from another org) — keep them visible as warnings, not error events.
        // 401 stays an error: a systemic 401 storm means broken auth and must
        // page someone, even at the cost of some post-deploy stale-tab noise —
        // unless embedded recovery has claimed it, which makes it a routine
        // lapsed-cookie event the app repairs by itself.
        // Other 4xx (400/422/…) mean the client sent a bad request: real bugs.
        // A refusal the caller declared expected (`expectedRefusals`, #1751) —
        // e.g. 409 START_PRICE_CHANGED (#1396), the price of a manual start grew
        // while the confirmation was open, or the closed list of the deal CRM
        // task's refusals (#766): an answer the screen explains, not a failure,
        // and Sentry has no use for it. The declaration lives with the caller
        // of the endpoint, not here.
        const isDeclaredRefusal = isExpectedRefusal(
          response.status,
          errorData,
          expectedRefusals
        );
        const isExpectedClientError =
          [403, 404].includes(response.status) ||
          embeddedRecoveryStarted ||
          isDeclaredRefusal;
        (isExpectedClientError ? logWarning : logError)('API request failed', {
          module: 'API',
          function: 'fetchWithRetry',
          status: response.status,
          message: errorMessage,
          hasErrorData: errorData !== null,
          // Same verdict as the flag on the error, carried into `extra` so the
          // captureMessage door is filterable on the decision rather than on
          // the server's wording.
          embeddedRecoveryHandled: embeddedRecoveryStarted,
        });

        throw error;
      }

      // For retryable errors (5xx), try to extract detailed message before retrying
      let retryableErrorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let retryableErrorData: unknown = null;
      try {
        const clonedResponse = response.clone();
        retryableErrorData = await clonedResponse.json();
        retryableErrorMessage =
          extractApiErrorMessage(retryableErrorData) || retryableErrorMessage;
      } catch {
        // Failed to parse, use default message
      }
      const retryableError = new ApiError(
        retryableErrorMessage,
        response.status,
        response.statusText,
        response
      );
      // The body is parsed for the message anyway — keep it on the error, as
      // for a 4xx, so a caller tells a 503 `access_policy_unavailable` from
      // an edge's bare 503 by its code and not by a missing body.
      if (retryableErrorData !== null && typeof retryableErrorData === 'object') {
        retryableError.data = retryableErrorData;
      }
      lastError = retryableError;
    } catch (error) {
      // Network errors or other fetch failures
      if (isAbortError(error)) {
        throw error;
      }
      // A verdict on the answer, not a failure of the connection: the repeat
      // belongs to the caller (React Query retries it once), which asks with
      // the revision now known — this loop would resend the old one.
      if (error instanceof StaleAccessRevisionError) {
        throw error;
      }
      // The verdict thrown a few lines above, on its way out. `throw error` for
      // a non-retryable status happens INSIDE this try, so it landed here and
      // became `lastError` — and the loop repeated the request it had just
      // decided not to repeat. Measured: a GET answered 401 produced four
      // requests, not one. Only a status-bearing ApiError qualifies; a network
      // failure has no status and stays retryable.
      if (error instanceof ApiError && !isRetryableError(error.status)) {
        throw error;
      }
      lastError = error instanceof Error ? error : new Error('Unknown error');
    }

    // Check if we should retry: only for idempotent methods or requests with Idempotency-Key
    if (!allowRetry || attempt === maxRetries) {
      break;
    }

    // Wait before retrying (exponential backoff)
    await new Promise((resolve) => setTimeout(resolve, getRetryDelay(attempt)));
  }

  // All retries exhausted
  throw lastError || new Error('Request failed after retries');
}

/**
 * Typed JSON fetch with retry logic
 */
export async function fetchJSON<T = unknown>(
  url: string,
  options?: RequestInit,
  maxRetries = 3,
  fence: AccessFence | null = null,
  expectedRefusals: readonly ExpectedRefusal[] = []
): Promise<T> {
  const resolvedUrl = resolveApiUrl(url);
  try {
    const response = await fetchWithRetry(
      resolvedUrl,
      options,
      maxRetries,
      fence,
      expectedRefusals
    );

    try {
      if (response.status === 204) {
        return undefined as T;
      }
      const text = await response.text();
      if (!text.trim()) {
        return undefined as T;
      }
      const data = JSON.parse(text) as T;
      return data;
    } catch {
      throw new ApiError(
        'Failed to parse JSON response',
        response.status,
        response.statusText,
        response
      );
    }
  } catch (error) {
    // If error is ApiError with response, try to parse debug info
    if (error instanceof ApiError && error.response && !error.response.ok) {
      try {
        const data = (await error.response.clone().json()) as {
          debug?: unknown;
          error?: string;
        };
        // Attach parsed error data to ApiError for easier access
        if (data.debug || data.error) {
          (error as ApiError & { data?: unknown }).data = data;
        }
      } catch {
        // Failed to parse, ignore
      }
    }
    throw error;
  }
}

/**
 * POST request helper
 *
 * H7 FIX: Now includes X-Selected-Org-ID header automatically.
 */
export async function post<T = unknown>(
  url: string,
  body: unknown,
  options?: Omit<RequestInit, 'method' | 'body'>,
  maxRetries = 3
): Promise<T> {
  return fetchJSON<T>(
    url,
    {
      ...options,
      method: 'POST',
      headers: layerHeaders(getDefaultHeaders('POST'), toHeaderRecord(options?.headers)),
      body: JSON.stringify(body),
    },
    maxRetries
  );
}

/**
 * PATCH request helper
 *
 * H7 FIX: Now includes X-Selected-Org-ID header automatically.
 */
export async function patch<T = unknown>(
  url: string,
  body: unknown,
  options?: Omit<RequestInit, 'method' | 'body'>,
  maxRetries = 3
): Promise<T> {
  return fetchJSON<T>(
    url,
    {
      ...options,
      method: 'PATCH',
      headers: layerHeaders(getDefaultHeaders('PATCH'), toHeaderRecord(options?.headers)),
      body: JSON.stringify(body),
    },
    maxRetries
  );
}

/**
 * DELETE request helper
 *
 * H7 FIX: Now includes X-Selected-Org-ID header automatically.
 */
export async function del<T = unknown>(
  url: string,
  options?: Omit<RequestInit, 'method'>,
  maxRetries = 3
): Promise<T> {
  return fetchJSON<T>(
    url,
    {
      ...options,
      method: 'DELETE',
      headers: layerHeaders(
        getDefaultHeaders('DELETE'),
        toHeaderRecord(options?.headers)
      ),
    },
    maxRetries
  );
}

/**
 * GET request helper (convenience wrapper)
 *
 * H7 FIX: Now includes X-Selected-Org-ID header automatically.
 */
export async function get<T = unknown>(
  url: string,
  options?: Omit<RequestInit, 'method'>,
  maxRetries = 3
): Promise<T> {
  return fetchJSON<T>(
    url,
    {
      ...options,
      method: 'GET',
      headers: layerHeaders(getDefaultHeaders('GET'), toHeaderRecord(options?.headers)),
    },
    maxRetries
  );
}

/**
 * Тело ответа с ошибкой API (`{ code, … }`), если его можно прочитать — одно
 * чтение на все экраны, которым нужен код отказа (критерии, черновик FAQ,
 * отчёт по критериям, набор тем).
 *
 * Fetch-слой кладёт разобранное JSON-тело в `ApiError.data` и у 4xx, и у 5xx;
 * тело, которое он не разобрал, остаётся непрочитанным в `response`. Код нужен
 * в обоих случаях: «слишком долго» и «сбой» у проверки различаются именно им.
 */
export async function apiErrorBody(
  error: unknown
): Promise<Record<string, unknown> | null> {
  if (!(error instanceof ApiError)) return null;
  if (isObject(error.data)) return error.data;
  if (!error.response) return null;
  try {
    const body: unknown = await error.response.clone().json();
    return isObject(body) ? body : null;
  } catch {
    return null;
  }
}

/** A JSON object (not an array, not null) — the shape of an error body. */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Generic error handler for displaying user-friendly messages
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}

/**
 * Create authenticated headers for API requests
 * Includes Authorization (Clerk JWT) and X-Selected-Org-ID (multi-org support)
 */
export async function createAuthHeaders(
  token: string,
  selectedOrgId: string,
  method?: string
): Promise<HeadersInit> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Selected-Org-ID': selectedOrgId,
  };
  const normalizedMethod = normalizeMethod(method);
  if (['POST', 'PUT', 'PATCH'].includes(normalizedMethod)) {
    headers['Content-Type'] = 'application/json';
  }

  // Add CSRF token for state-changing operations
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(normalizedMethod)) {
    const csrfToken = await getCSRFToken();
    headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
}

export type { AuthenticatedFetchOptions, ExpectedRefusal } from './request-headers';

/**
 * The session token, plus the means to replace it.
 *
 * A bare string was the previous parameter, and it made one failure
 * unrecoverable by construction: Clerk rotates the JWT roughly every minute,
 * so a request begun just before expiry arrives just after it and comes back
 * 401 — with no way for this function to obtain a newer token, because it was
 * handed a value rather than a source. The XHR upload path has always been
 * able to repeat a 401 (it calls `getAuthData()` again per attempt); this is
 * the same capability on the fetch path, and the two transports are meant to
 * agree.
 */
export interface SessionToken {
  /** Sent on the first attempt. */
  value: string;
  /**
   * Mint a replacement after a 401. Returning the same string means no newer
   * token exists, and the repeat is skipped — a repeat identical to the
   * request that just failed is not a retry, it is a second failure.
   */
  refresh?: () => Promise<string>;
}

/**
 * Authenticated fetch wrapper - automatically adds auth headers
 * Use this for all API calls to AICA backend
 *
 * A 401 is repeated once with a freshly minted token. This is safe for every
 * method, including the non-idempotent ones: the auth middleware refuses
 * before the handler runs, so nothing was committed and a repeat cannot
 * duplicate it. Same axis as the upload path — see `decideUploadRetry`.
 */
export async function authenticatedFetch<T = unknown>(
  url: string,
  token: SessionToken,
  selectedOrgId: string,
  options?: AuthenticatedFetchOptions,
  maxRetries = 3
): Promise<T> {
  const { extraHeaders, accessRead, expectedRefusals, ...init } = options ?? {};
  const method = init.method || 'GET';

  // Resolve /api/* paths against the configured base URL (default: same
  // origin — the ui-worker proxies /api/* to edge-api; in dev, Vite proxies
  // it to the local edge-api).
  let finalUrl = resolveApiUrl(url);

  // Add orgId to URL as cache-busting param for GET requests
  // This ensures browser cache is keyed by org, so switching orgs gets fresh data
  // while same-org requests can still use cache
  if (method === 'GET') {
    const separator = finalUrl.includes('?') ? '&' : '?';
    finalUrl = `${finalUrl}${separator}_org=${selectedOrgId}`;
  }

  const send = async (bearer: string): Promise<T> => {
    const authHeaders = (await createAuthHeaders(
      bearer,
      selectedOrgId,
      method
    )) as Record<string, string>;
    // One fence per attempt, opened after the last await before the request
    // (the CSRF token may be refreshed above): the revision known when THIS
    // request leaves is the one it is sent with and judged against.
    const fence = beginAccessFence(selectedOrgId, {
      method,
      url: finalUrl,
      read: accessRead,
    });
    return fetchJSON<T>(
      finalUrl,
      {
        ...init,
        // Caller extras first, auth on top: `Idempotency-Key` gets through,
        // `Authorization`/`X-Selected-Org-ID`/`X-Access-Revision` cannot be
        // overridden.
        headers: layerHeaders(extraHeaders, {
          ...authHeaders,
          ...accessRevisionRequestHeaders(fence),
        }),
        credentials: 'include', // Include cookies for CSRF
      },
      maxRetries,
      fence,
      expectedRefusals
    );
  };

  try {
    return await send(token.value);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401 || !token.refresh) {
      throw error;
    }
    const fresh = await token.refresh();
    if (fresh === token.value) {
      throw error;
    }
    return send(fresh);
  }
}
